import React from "react";
import { Currency, Payment, PaymentStatus, PaymentType, ProviderList, Settings } from "../../http/protocol";
import { Form } from "../form/Form";
import {FormGroup} from "../form/FormGroup";
import { LoadingViewState, RouterView } from "../RouterView";
import {RouteComponentProps, withRouter} from "react-router";
import { PaymentService } from "../../services/PaymentService";
import { ProviderService } from "../../services/ProviderService";
import { getContainer } from "../../ioc/IOCSetup";
import { ErrorModal } from "../../util/ErrorModal";
import { Submit } from "../form/Submit";
import { FormInput, FormMap, StaticFormValue } from "../form/FormInput";
import { Validators } from "../form/Validators";
import { SelectInput } from "../form/SelectInput";
import { SettingsService } from "../../services/SettingsService";
import { UserService } from "../../services/UserService";
import './PaymentEdit.scss';
import { WalletService } from "../../services/WalletService";
import { ConfirmModal } from "../../util/ConfirmModal";
import { PaymentProviderService } from "../../services/PaymentProviderService";

interface State extends LoadingViewState {
    payment? : Payment;
    validationMap? : any;
    error?: boolean;
    errorMessage?: string;
    confirmModal?:boolean;
    confirmMessage?: string;
    currencies? : Currency[];
    providerNames? : string[];
    expanded?: boolean;
    selectedCurrency?: Currency;
    fractionalDigits?:number;
}

interface Props extends RouteComponentProps{ 

}

export class PaymentEdit extends RouterView<Props, State>{
    
    service : PaymentService;
    settingsService : SettingsService;
    userService: UserService;
    walletService: WalletService;
    paymentProviderSer: PaymentProviderService;
 

    constructor(props : Props){
        super(props);
        this.service = getContainer().getPaymentService();
        this.settingsService = getContainer().getSettingsService();
        this.userService = getContainer().getUserService();
        this.walletService = getContainer().getWalletService();
        this.paymentProviderSer = getContainer().getPaymentProviderService();

        this.state = {payment : {type: PaymentType.DEPOSIT, status: PaymentStatus.COMPLETED}, 
            validationMap : {}, 
            error: false, 
            currencies : [], 
            providerNames : [],
            expanded:false, 
            selectedCurrency: undefined ,
            fractionalDigits:2};
    }

    getTitle(): string {
        return "Create Payment Deposit";
    }

    componentDidUpdate(prevProps : RouteComponentProps, prevState : State) {
        if((prevProps.match.params as any).id !== (this.props.match.params as any).id)  {
            this.refreshContent();
        }
    }

    loadContent(): void | Promise<any> {
        let array: string[] = [];
        if((this.props.match.params as any).id === "new") {
            this.paymentProviderSer.listManualPaymentProviders().then((response:ProviderList)=>{
                this.setState({providerNames:response!.providers});
            })
        }
        return this.settingsService.getSettings().then( (s : Settings) => {
            this.setState({currencies:s.currencies});
        }) 
    }

    savePayment(){
        this.setState({confirmModal:false});
        if(!this.state.payment) return;  

        this.service.getPaymentByExternalPaymentId(this.state.payment.externalPaymentId!).then((response:any)=>{
            this.setState({error:true, errorMessage: "External payment id is in the payment records, select another, the userId that use this External payment id  is the :"+response.id!});
        }).catch(()=>{
            this.setState({error:false});
            this.service.save(this.state.payment!).then((a : Payment)=> {
                if((this.props.match.params as any).id=== "new") {
                    this.navigate("/payment/" + a.id!);
                }
            }).catch((error=>{
                this.setState({error:true, errorMessage: "Error saving payment: ".concat(error.message)});
            }))
        });
    }

    onSubmit()  {        
        this.setState({confirmModal:true, confirmMessage:"Confirm payment of user "+this.state.payment!.userId+" with an amount of "+this.state.payment!.amount+" "+this.state.payment!.currency+" with External Id "+this.state.payment!.externalPaymentId});
    }

    formDidUpdate(formModel: Payment, validationMap?: any) {
        this.setState({payment : formModel, validationMap : validationMap})
    }


    addAttribute(key:string,value:string) {
        if(!this.state.payment) return;
        let kvp : any= {};
        kvp[key] = value;
        let attributes = Object.assign({}, this.state.payment.attributes, kvp);
        this.setState({ payment : Object.assign({}, this.state.payment , { attributes : attributes})})
    }
    removeAttribute(key : string) {
        if(!this.state.payment) return;
        let attributes = Object.assign({}, this.state.payment.attributes);
        delete attributes[key];
        this.setState({ payment : Object.assign({}, this.state.payment , { attributes : attributes})})
    }

    onCurrencyChange(currency:any){
        //get currency from currency array
        let selectedCurrency = this.state.currencies.find((c:Currency)=>c.code===currency);
        this.setState({selectedCurrency:selectedCurrency});
    }

    
    renderContent() {
        if(!this.state.payment) {
            return <span></span>
        }
        const providers : string[] = this.state.providerNames || [];
        let fractionalDigits = this.state.selectedCurrency && this.state.selectedCurrency.fractionalDigits ? this.state.selectedCurrency.fractionalDigits: 2;
        return (<div>
                <div className="wrapper wrapper-content animated fadeInRight">
                <Form className="form form-horizontal" validationMap={this.state.validationMap} modelObject={this.state.payment} formListener={this}>
                            
                            <div className="row">
                                <div className="col-lg-12">
                                    <div className="card-box">
                                        <FormGroup className="form-group">
                                            <label className="col-sm-2 control-label">External Payment Id</label>
                                            <div className="col-sm-10">
                                                <FormInput  model="externalPaymentId" validators={[Validators.required()]} type="text"  className="form-control" />
                                                <span className={"help"}>Unique Id taken from the provider system. The system will check that no other payment with the same id has been entered before.</span>
                                            </div>
                                        </FormGroup>
                                        <div className="hr-line-dashed"></div>
                                        <FormGroup className="form-group">
                                            <label className="col-sm-2 control-label">User ID</label>
                                            <div className="col-sm-10">
                                                <FormInput  model="userId" validators={[Validators.required()]} type="text"  className="form-control" />
                                                <span className={"help"}>Id of user to make deposit to</span>
                                            </div>
                                        </FormGroup>
                                        <div className="hr-line-dashed"></div>
                                        <FormGroup className="form-group">
                                            <label className="col-sm-2 control-label">Provider</label>
                                            <div className="col-sm-10">
                                                <SelectInput name="provider" optional={true} className="form-control" model="provider"  values={providers} validators={[Validators.required()]} />
                                                <span className={"help"}>Choose a provider from the active set (i.e. TAG, GBL, etc)</span>
                                            </div>
                                        </FormGroup>
                                        <div className="hr-line-dashed"></div>
                                        <FormGroup className="form-group">
                                            <label className="col-sm-2 control-label">Currency</label>
                                            <div className="col-sm-10">
                                                <SelectInput name="currency" optional={true} className="form-control" model="currency"  values={this.state.currencies.map( (a:Currency)=> a.code)} validators={[Validators.required()]}
                                                onClickCapture={((currency:any)=>{
                                                    this.onCurrencyChange(currency.target.value);
                                                })} />
                                                <span className={"help"}>Type currency</span>
                                            </div>
                                        </FormGroup> 
                                        <div className="hr-line-dashed"></div>
                                        <FormGroup className="form-group">
                                            <label className="col-sm-2 control-label">Amount</label>
                                            <div className="col-sm-10">
                                                <FormInput  model="amount" validators={[Validators.required(), Validators.regExp(new RegExp("^\\d+(\\.\\d{1,"+fractionalDigits+"})?$"))]}  type="number"
                                                onKeyDown={(down:any)=>{
                                                    if (down.which === 38 || down.which === 40) {
                                                        down.preventDefault();
                                                    }
                                                }} className="form-control"/>
                                                <span className={"help"}>{"Use a positive amount. Make sure to choose a currrency first. Max decimals are determined by the currency: "+ fractionalDigits} </span>
                                            </div>
                                        </FormGroup>
                                        <div className="hr-line-dashed"></div>
                                        <FormGroup className="form-group">
                                            <label className="col-sm-2 control-label">Merchant Id</label>
                                            <div className="col-sm-10">
                                                <FormInput  model="merchantTransactionId"  type="text"  className="form-control" />
                                                <span className={"help"}>Optional field, value must be taken from the payment provider service unique identifier to be used for reconciliation</span>
                                            </div>
                                        </FormGroup>
                                        <div className="hr-line-dashed"></div>
                                        <FormGroup className="form-group">
                                            <label className="col-sm-2 control-label">Aggregator Id</label>
                                            <div className="col-sm-10">
                                                <FormInput  model="agregatorTransactionId" type="text" validators={[Validators.required()]} optional={true} className="form-control" />
                                                <span className={"help"}>Required field, value must be taken from the payment aggregator service unique identifier to be used for reconciliation</span>
                                            </div>
                                        </FormGroup>            
                                    </div>
                                </div>
                            </div>  
                            <div className="row">
                                <div className="col-lg-12">
                                    <div className="card-box expandable-box">
                                        {this.state.expanded && (
                                            <a className="expand-button float-right" onClick={() => this.setState({ expanded: false })}><i className="mdi mdi-minus"></i></a>
                                        )}
                                        {!this.state.expanded && (
                                            <a className="expand-button float-right" onClick={() => this.setState({ expanded: true })}><i className="mdi mdi-plus"></i></a>
                                        )}

                                        <h4 className="header-title mb-3" onClick={() => this.setState({ expanded: !this.state.expanded })}>Payment Attributes</h4>
                                        {this.state.expanded && (
                                            <FormGroup className="form-group">
                                                <div className="col-sm-10">   
                                                <FormMap 
                                                    mapModel="attributes"
                                                    removeAttribute={(key? : string)=> key && this.removeAttribute(key)}
                                                    addAttribute={(key?, value?)=> key && value && this.addAttribute(key,value)}
                                                    optional={true}
                                                />
                                                <span className={"help"}>Keys should not have spaces nor special characters</span>
                                                <span className={"help"}>Payment Attributes are used as a key value store to save custom data related to a payment the admin users might want to see when reviewing the payment in the future. ie: originalPaymentDate:20-12-2013</span>
                                                </div>
                                            </FormGroup>
                                            
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-lg-12">
                                    <div className="card-box">
                                        <div className="form-group">
                                            <div className="col-sm-12">
                                                <Submit className="btn  btn-primary">Save</Submit>
                                            </div>
                                        </div>

                                    </div>
                                </div>
                            </div>
                    </Form>
                </div>

                <ConfirmModal
                    show={this.state.confirmModal}
                    hideCancelButton={false}
                    heading={"Payment Confirmation"}
                    description={this.state.confirmMessage}
                    okLabel="OK"
                    onOk={
                        ()=> this.savePayment()
                    }
                    onCancel={()=> this.setState({confirmModal:false})}
                />
                
                <ErrorModal
                    show={this.state.error}
                    hideCancelButton={true}
                    heading="Oops, something went bonker!"
                    description={this.state.errorMessage}
                    okLabel="OK"
                    disableActions={true}
                    onOk={
                        ()=> this.setState({error:false})
                    }
                    onCancel={()=> this.setState({error:false})}
                />
        </div>)
    }

}



export default withRouter(PaymentEdit);